import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

type LatestImportResponse = {
  ok: boolean;
  import: {
    id: string;
    status: string;
    sourceType: 'csv' | 'document';
    totalRows: number;
    processedRows: number;
    failedRows: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  isActive: boolean;
  buildingWorkflowIds: string[];
};

/**
 * Hook to fetch and poll for active import status.
 * Polls every 5 seconds while an import is active.
 */
export function useActiveImport() {
  const query = useQuery<LatestImportResponse>({
    queryKey: ['workflow-imports', 'latest'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/workflows/imports/latest`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch latest import');
      }
      return response.json();
    },
    // Poll every 5 seconds while active
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.isActive ? 5000 : false;
    },
    // Don't refetch on window focus if import is already complete
    refetchOnWindowFocus: (query) => {
      const data = query.state.data;
      return data?.isActive ?? true;
    },
  });

  return {
    activeImport: query.data?.import ?? null,
    isActive: query.data?.isActive ?? false,
    sourceType: query.data?.import?.sourceType ?? 'csv',
    buildingWorkflowIds: new Set(query.data?.buildingWorkflowIds ?? []),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
